import { computed } from "vue";
import { storeToRefs } from "pinia";

import { useAuthStore } from "@/stores/Auth";
import { useBonusReportsStore } from "@/stores/BonusReports";

import { useCategoryFilter } from "@/composables/filters/category-filter";
import { useProductFilter } from "@/composables/filters/product-filter";
import { useTextFilter } from "@/composables/filters/text-filter";
import { useContentFilter } from "@/composables/filters/use-content-filter";

import { BonusReport } from "@tmi/types/entities";

export function useBonusReportFilters() {
    const { isAuthenticated } = storeToRefs( useAuthStore() );
    const bonusReportsStore = useBonusReportsStore();
    const { allBonusReports } = storeToRefs( bonusReportsStore );

    // ------------------------------------ FILTERS ------------------------------------
    // The categories that have been selected to limit the results (inclusive)
    const { filterOutItemsThatDoNotContainSelectedCategories } = useCategoryFilter();

    // The products that have been selected to limit the results (inclusive)
    const { filterOutItemsThatDoNotContainSelectedProducts } = useProductFilter();

    // The search text entered by the user
    const { filterOutItemsThatDoNotContainSearchText } = useTextFilter();

    const {
        filtered: filteredBonusReports,
        filteredPage: filteredBonusReportsPage,
        hasAnyResults: hasAnyBonusReportsResults,
        searchResultsLabel,
    } = useContentFilter< BonusReport >( {
        itemNoun: 'report',
        // Historical label was always plural: "N reports found".
        buildLabel: ( count: number ) => `${ count } reports found`,
        computeFiltered: () => {
            // Historical behavior: only shows results for authenticated users.
            if ( !isAuthenticated.value ) return [];

            const filtered = allBonusReports.value
                .filter( ( report: BonusReport ) => filterOutItemsThatDoNotContainSelectedCategories( report.categories ) )
                .filter( ( report: BonusReport ) => filterOutItemsThatDoNotContainSelectedProducts( report.products ) )
                .filter( ( report: BonusReport ) => filterOutItemsThatDoNotContainSearchText( [ report.title ] ) );

            // Sort must happen after the .filter chain to be stable across
            // recomputes; historically returned array is in place-sorted.
            return [ ...filtered ].sort(
                ( a: BonusReport, b: BonusReport ) => a.publishedDate < b.publishedDate ? 1 : -1
            );
        },
    } );

    return {
        // T/F based on if filteredBonusReportsPage has any items
        hasAnyBonusReportsResults,
        // '#### results found'
        searchResultsLabel,

        // The current single page sub-collection
        filteredBonusReportsPage,
        // All bonus reports that match the current filter
        filteredBonusReports,
    }
}

export type BonusReportFilters = ReturnType< typeof useBonusReportFilters >;
